import { PAGE_SIZE } from "constants/TableConstants";
import { useState } from "react";
import {
  useGetDocumentsByClaimIdQuery,
  useGetDocumentsByRREIdQuery,
  useGetDocumentsQuery,
} from "services/documents-api";
import type { RootState } from "services/store";
import { useQuery } from "utils/hooks";
import { useSelector } from "utils/redux";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore";

import type { ServiceType } from "../DocumentTypes";
import { useDocumentMutations } from "./useDocumentMutations";

type UseDocumentTableConfig = {
  fileId?: number;
  claimId?: number;
  rreId?: number;
  serviceId?: number;
  serviceType?: string;
  additionalParams?: Record<string, string>;
  sortBy?: string | null;
  extension?: string;
  queryFilters?: Record<string, string | string[]>;
};

/**
 * comprehensive hook for document table with pagination, search, filters, and mutations
 * used by Documents drawer component
 * supports file-based, claim-based, and RRE-based documents
 *
 * for simple document lists in modals, use useGetDocumentsQuery directly from services/documents-api
 * this hook includes drawer-specific behavior (skip when drawer closed) and URL-based state
 */
export const useDocumentTable = (config: UseDocumentTableConfig) => {
  const {
    fileId,
    claimId,
    rreId,
    serviceId,
    serviceType,
    additionalParams,
    sortBy,
    extension,
    queryFilters,
  } = config;

  const query = useQuery();
  const { getFilters } = useUrlStore();
  const [page, setPage] = useState(1);

  const showDocumentsDrawer = useSelector(
    (state: RootState) => state.drawer.showDocumentsDrawer,
  );

  const documentSearchTerm = query.get(GLOBAL_SEARCH_PARAM);
  const urlFilters = getFilters();
  // merge URL filters with drawer-specific query filters
  const filters = {
    ...urlFilters,
    ...(queryFilters || {}),
    ...(extension && { extension }),
  };

  // file-based documents query
  const fileDocumentsQuery = useGetDocumentsQuery(
    {
      fileId: fileId?.toString() || "",
      searchTerm: documentSearchTerm || undefined,
      serviceId: serviceId?.toString(),
      serviceType: serviceType as ServiceType,
      sortBy: sortBy || "-id",
      filters,
      limit: PAGE_SIZE.toString(),
      offset: ((page - 1) * PAGE_SIZE).toString(),
      additionalParams,
    },
    {
      skip: !fileId || !showDocumentsDrawer,
      refetchOnMountOrArgChange: true,
    },
  );

  // claim-based documents query
  const claimDocumentsQuery = useGetDocumentsByClaimIdQuery(
    {
      claimId: claimId?.toString() || "",
      searchTerm: documentSearchTerm || undefined,
      sortBy: sortBy || "-id",
      filters,
      limit: PAGE_SIZE.toString(),
      offset: ((page - 1) * PAGE_SIZE).toString(),
    },
    {
      skip: !claimId || !showDocumentsDrawer,
      refetchOnMountOrArgChange: true,
    },
  );

  // RRE-based documents query
  const rreDocumentsQuery = useGetDocumentsByRREIdQuery(
    {
      rreId: rreId?.toString() || "",
      searchTerm: documentSearchTerm || undefined,
      sortBy: sortBy || "-id",
      filters,
      limit: PAGE_SIZE.toString(),
      offset: ((page - 1) * PAGE_SIZE).toString(),
    },
    {
      skip: !rreId || !showDocumentsDrawer,
      refetchOnMountOrArgChange: true,
    },
  );

  // use the appropriate query based on what's provided
  const activeQuery = claimId
    ? claimDocumentsQuery
    : rreId
      ? rreDocumentsQuery
      : fileDocumentsQuery;
  const {
    data = { results: [], count: 0 },
    isLoading,
    isError,
    error,
    isFetching,
  } = activeQuery;

  // get mutation handlers
  const mutations = useDocumentMutations({
    fileId,
    claimId,
    rreId,
    serviceId,
    serviceType: serviceType as ServiceType,
    additionalParams,
  });

  return {
    documents: data.results,
    totalCount: data.count,
    isLoading,
    isError,
    error,
    isFetching,
    documentSearchTerm,
    page,
    setPage,
    ...mutations,
  };
};
